import { defineStore } from 'pinia';
import { getAllDepartments, addProductAPI, getAllProducts, deleteProductAPI, updateProductAPI } from '@/utils/api';
import { defaultDepartment, viviposCategoryToDepartment } from '@/store/management/departmentsStore';
import type { Department } from '@/store/management/departmentsStore';
import type { ProductGroup, ViviposProductGroup } from '@/store/management/productGroupsStore';

export interface ViviposProduct {
    MID: number;
    ID: string;
    CateNo?: string | null;
    No?: string | null;
    Name?: string | null;
    Barcode?: string | null;
    Rate?: string | null;
    CondGroup?: string | null;
    BuyPrice?: number | null;
    BuyVendor?: string | null;
    BuyMOQ: number;
    Stock?: number | null;
    MinStock?: number | null;
    Memo?: string | null;
    MinSaleQty?: number | null;
    SetMenu?: string | null;
    LevelEnable1?: boolean | null;
    PriceLevel1?: number | null;
    Halo1?: number | null;
    Lalo1?: number | null;
    LevelEnable2?: boolean | null;
    PriceLevel2?: number | null;
    Halo2?: number | null;
    Lalo2?: number | null;
    LevelEnable3?: boolean | null;
    PriceLevel3?: number | null;
    Halo3?: number | null;
    Lalo3?: number | null;
    LevelEnable4?: boolean | null;
    PriceLevel4?: number | null;
    Halo4?: number | null;
    Lalo4?: number | null;
    LevelEnable5?: boolean | null;
    PriceLevel5?: number | null;
    Halo5?: number | null;
    Lalo5?: number | null;
    LevelEnable6?: boolean | null;
    PriceLevel6?: number | null;
    Halo6?: number | null;
    Lalo6?: number | null;
    LevelEnable7?: boolean | null;
    PriceLevel7?: number | null;
    Halo7?: number | null;
    Lalo7?: number | null;
    LevelEnable8?: boolean | null;
    PriceLevel8?: number | null;
    Halo8?: number | null;
    Lalo8?: number | null;
    LevelEnable9?: boolean | null;
    PriceLevel9?: number | null;
    Halo9?: number | null;
    Lalo9?: number | null;
    LinkGroup?: string | null;
    AutoMaintainStock?: boolean | null;
    ReturnStock?: boolean | null;
    ForceCondiment?: boolean | null;
    ForceMemo?: boolean | null;
    Single?: boolean | null;
    Visible?: boolean | null;
    ButtonColor?: string | null;
    FontSize?: string | null;
    AgeVerification?: boolean | null;
    Created?: number | null;
    Modified?: number | null;
    AltName1?: string | null;
    AltName2?: string | null;
    ManualAdjustmentOnly?: boolean | null;
    AppendEmptyBtns?: number | null;
    DisplayOrder?: number | null;
    Tare?: number | null;
    SaleUnit?: string | null;
    PurchaseUnit?: string | null;
    Scale?: boolean | null;
    MaxStock?: number | null;
    ScaleMultiplier?: number | null;
    ScalePrecision?: number | null;
    DisplayMode?: number | null;
    NonDiscountable?: boolean | null;
    NonSurchargeable?: boolean | null;
    LinkProduct?: string | null;
    StockUnit?: string | null;
    StockConversion?: number | null;
    ForceVariation?: boolean | null;
    StockSource?: boolean | null;
    StockLocation?: string | null;
    AltLanguage?: string | null;
    ServiceCharge?: string | null;
    PicThumb?: string | null;
    NonMembershipDiscountAllowed?: boolean | null;
}

export interface ViviposProductEdit {
    ViviposProduct: ViviposProduct;
    ViviposSetItem?: ViviposSetItem[] | null;
    ViviposPluGroup?: ViviposProductGroup[] | null;
}

export interface ViviposSetItem {
    MID: number;
    ID: string;
    PlusetNo: string;
    Label?: string | null;
    PresetNo?: string | null;
    Quantity: number;
    BasePrice?: number | null;
    LinkGroupID?: string | null;
    Reduction?: boolean | null;
    Created?: number | null;
    Modified?: number | null;
    Sequence?: number | null;
}

export interface Product {
    basic: Basic;
    stock: Stock;
    scale: Scale;
    setItems: SetItems[];
    priceLevels: PriceLevel[];
    condiments: Condiments;
    linkGroups: LinkGroups[];
    appearance: Appearance;
    variation: Variation;
    miscellaneous: Miscellaneous;
}

export interface Appearance {
    image: string;
    buttonColor: string;
    fontSizes: string;
    displayMode: string;
}

export interface Basic {
    id: string;
    department: string;
    productNumber: string;
    name: string;
    barcode: string;
    taxRate: string;
    serviceCharge: string;
    alternateName: string;
    alternateName2: string;
    displayOrder: number;
    emptyBtn: number;
    singleItemSale: boolean;
    showOnMenu: boolean;
    altDisplayLang: String[];
}

export interface Stock {
    autoStock: boolean;
    returnsCanBeRestocked: boolean;
    stockSource: boolean;
    stockLocation: string;
    stockLowWatermark: number;
    stockHighWatermark: number;
    saleToStockUnitRatio: number;
    currentStockLevel: number;
    saleUnit: string;
    stockUnit: string;
}

export interface Scale {
    scaleItem: boolean;
    weightDecimalPlaces: number;
    scaleMultiplier: number;
    tare: number;
}

export interface SetItems {
    id: string;
    label: string;
    basePrice: number;
    quantity: number;
    allowReduction: boolean;
    linkGroup: LinkGroups;
    presetItem: Product;
}

export interface PriceLevel {
    priceEnable: boolean;
    priceLevel: string;
    price: number;
    HALO: number;
    LALO: number;
}

export interface Condiment {
    id: string;
    name: string;
}

export interface Condiments {
    required: boolean;
    condimentList: Condiment[];
}

export interface LinkGroups {
    id: string;
    name: string;
    appearance: Appearance;
}

export interface VariationItem {
    id: string;
    variation: string;
    productData: Product;
}

export interface Variation {
    required: boolean;
    sourceProduct: boolean;
    variationItem: VariationItem[];
}

export interface Miscellaneous {
    requiredMemo: boolean;
    memo: string;
    ageVerificationRequired: boolean;
    noMembershipDiscountAllowed: boolean;
    nonDiscountable: boolean;
    nonSurchargeable: boolean;
}

export const defaultProduct: Product = {
    appearance: {
        image: '',
        buttonColor: '',
        fontSizes: '',
        displayMode: ''
    },
    basic: {
        id: '',
        department: '',
        productNumber: '',
        name: '',
        barcode: '',
        taxRate: '',
        serviceCharge: '',
        alternateName: '',
        alternateName2: '',
        displayOrder: 0,
        emptyBtn: 0,
        singleItemSale: false,
        showOnMenu: false,
        altDisplayLang: []
    },
    stock: {
        autoStock: false,
        returnsCanBeRestocked: false,
        stockSource: false,
        stockLocation: '',
        stockLowWatermark: 0,
        stockHighWatermark: 0,
        saleToStockUnitRatio: 0,
        currentStockLevel: 0,
        saleUnit: '',
        stockUnit: ''
    },
    scale: {
        scaleItem: false,
        weightDecimalPlaces: 0,
        scaleMultiplier: 0,
        tare: 0
    },
    setItems: [],
    priceLevels: [],
    condiments: {
        required: false,
        condimentList: []
    },
    linkGroups: [],
    variation: {
        required: false,
        sourceProduct: false,
        variationItem: []
    },
    miscellaneous: {
        requiredMemo: false,
        memo: '',
        ageVerificationRequired: false,
        noMembershipDiscountAllowed: false,
        nonDiscountable: false,
        nonSurchargeable: false
    }
};
export const defaultSetItem: SetItems = {
    id: '',
    label: '',
    basePrice: 0,
    quantity: 0,
    allowReduction: false,
    linkGroup: {} as LinkGroups,
    presetItem: {} as Product,
}

export function productToVivipos(product: Product): ViviposProductEdit {
    return {
        ViviposProduct: {
            MID: 1,
            // === Baic product information ===
            ID: product.basic.id,
            CateNo: product.basic.department,
            No: product.basic.productNumber,
            Name: product.basic.name,
            Barcode: product.basic.barcode,
            Rate: product.basic.taxRate,
            ServiceCharge: product.basic.serviceCharge,
            AltName1: product.basic.alternateName,
            AltName2: product.basic.alternateName2,
            AltLanguage: product.basic.altDisplayLang.join('|'),
            DisplayOrder: product.basic.displayOrder,
            AppendEmptyBtns: product.basic.emptyBtn,
            Single: product.basic.singleItemSale,
            Visible: product.basic.showOnMenu,

            // === Stock information ===
            AutoMaintainStock: product.stock.autoStock,
            ReturnStock: product.stock.returnsCanBeRestocked,
            StockSource: product.stock.stockSource,
            StockLocation: product.stock.stockLocation,
            MinStock: product.stock.stockLowWatermark,
            MaxStock: product.stock.stockHighWatermark,
            StockConversion: product.stock.saleToStockUnitRatio,
            Stock: product.stock.currentStockLevel,
            // Sale to Stock Unit Ratio missing
            SaleUnit: product.stock.saleUnit,
            StockUnit: product.stock.stockUnit,

            // === Scale ===
            Scale: product.scale.scaleItem,
            ScaleMultiplier: product.scale.scaleMultiplier,
            ScalePrecision: product.scale.weightDecimalPlaces,
            Tare: product.scale.tare,

            // === Set items ===
            SetMenu: product.setItems.map(item => item.id).join('|'),

            // === Appearance ===
            ButtonColor: product.appearance.buttonColor,
            FontSize: product.appearance.fontSizes,
            DisplayMode: Number(product.appearance.displayMode) || null,
            PicThumb: product.appearance.image,

            // === Price levels ===
            LevelEnable1: product.priceLevels[0]?.priceEnable ?? false,
            PriceLevel1: product.priceLevels[0]?.price ?? null,
            Halo1: product.priceLevels[0]?.HALO ?? null,
            Lalo1: product.priceLevels[0]?.LALO ?? null,
            LevelEnable2: product.priceLevels[1]?.priceEnable ?? false,
            PriceLevel2: product.priceLevels[1]?.price ?? null,
            Halo2: product.priceLevels[1]?.HALO ?? null,
            Lalo2: product.priceLevels[1]?.LALO ?? null,
            LevelEnable3: product.priceLevels[2]?.priceEnable ?? false,
            PriceLevel3: product.priceLevels[2]?.price ?? null,
            Halo3: product.priceLevels[2]?.HALO ?? null,
            Lalo3: product.priceLevels[2]?.LALO ?? null,
            LevelEnable4: product.priceLevels[3]?.priceEnable ?? false,
            PriceLevel4: product.priceLevels[3]?.price ?? null,
            Halo4: product.priceLevels[3]?.HALO ?? null,
            Lalo4: product.priceLevels[3]?.LALO ?? null,
            LevelEnable5: product.priceLevels[4]?.priceEnable ?? false,
            PriceLevel5: product.priceLevels[4]?.price ?? null,
            Halo5: product.priceLevels[4]?.HALO ?? null,
            Lalo5: product.priceLevels[4]?.LALO ?? null,
            LevelEnable6: product.priceLevels[5]?.priceEnable ?? false,
            PriceLevel6: product.priceLevels[5]?.price ?? null,
            Halo6: product.priceLevels[5]?.HALO ?? null,
            Lalo6: product.priceLevels[5]?.LALO ?? null,
            LevelEnable7: product.priceLevels[6]?.priceEnable ?? false,
            PriceLevel7: product.priceLevels[6]?.price ?? null,
            Halo7: product.priceLevels[6]?.HALO ?? null,
            Lalo7: product.priceLevels[6]?.LALO ?? null,
            LevelEnable8: product.priceLevels[7]?.priceEnable ?? false,
            PriceLevel8: product.priceLevels[7]?.price ?? null,
            Halo8: product.priceLevels[7]?.HALO ?? null,
            Lalo8: product.priceLevels[7]?.LALO ?? null,
            LevelEnable9: product.priceLevels[8]?.priceEnable ?? false,
            PriceLevel9: product.priceLevels[8]?.price ?? null,
            Halo9: product.priceLevels[8]?.HALO ?? null,
            Lalo9: product.priceLevels[8]?.LALO ?? null,

            // === Condiments ===
            ForceCondiment: product.condiments.required,
            CondGroup: product.condiments.condimentList.map(c => c.id).join('|'),

            // === Link groups ===
            LinkGroup: product.linkGroups.map(lg => lg.id).join('|'),

            // === Variation ===
            ForceVariation: product.variation.required,

            // === Miscellaneous ===
            ForceMemo: product.miscellaneous.requiredMemo,
            Memo: product.miscellaneous.memo,
            AgeVerification: product.miscellaneous.ageVerificationRequired,
            NonDiscountable: product.miscellaneous.nonDiscountable,
            NonSurchargeable: product.miscellaneous.nonSurchargeable,
            NonMembershipDiscountAllowed: product.miscellaneous.noMembershipDiscountAllowed,


            BuyMOQ: 0,
            BuyPrice: null,
            BuyVendor: null,
            LinkProduct: null,
            ManualAdjustmentOnly: null,
            PurchaseUnit: null,
        },
        ViviposSetItem: product.setItems.map(setItemToVivipos),
    };
}

export function viviposToProduct(vivipos: ViviposProductEdit, productList: Product[]): Product {
    return {
        appearance: {
            image: vivipos.ViviposProduct?.PicThumb ?? '',
            buttonColor: vivipos.ViviposProduct?.ButtonColor ?? '',
            fontSizes: vivipos.ViviposProduct?.FontSize ?? '',
            displayMode: vivipos.ViviposProduct?.DisplayMode ? String(vivipos.ViviposProduct?.DisplayMode) : ''
        },
        basic: {
            id: vivipos.ViviposProduct?.ID ?? '',
            department: vivipos.ViviposProduct?.CateNo ?? '',
            productNumber: vivipos.ViviposProduct?.No ?? '',
            name: vivipos.ViviposProduct?.Name ?? '',
            barcode: vivipos.ViviposProduct?.Barcode ?? '',
            taxRate: vivipos.ViviposProduct?.Rate ?? '',
            serviceCharge: vivipos.ViviposProduct?.ServiceCharge ?? '',
            alternateName: vivipos.ViviposProduct?.AltName1 ?? '',
            alternateName2: vivipos.ViviposProduct?.AltName2 ?? '',
            displayOrder: vivipos.ViviposProduct?.DisplayOrder ?? 0,
            emptyBtn: vivipos.ViviposProduct?.AppendEmptyBtns ?? 0,
            singleItemSale: vivipos.ViviposProduct?.Single ?? false,
            showOnMenu: vivipos.ViviposProduct?.Visible ?? false,
            altDisplayLang: vivipos.ViviposProduct?.AltLanguage ? vivipos.ViviposProduct?.AltLanguage.split('|') : []
        },
        stock: {
            autoStock: vivipos.ViviposProduct?.AutoMaintainStock ?? false,
            returnsCanBeRestocked: vivipos.ViviposProduct?.ReturnStock ?? false,
            stockSource: vivipos.ViviposProduct?.StockSource ?? false,
            stockLocation: vivipos.ViviposProduct?.StockLocation ?? '',
            stockLowWatermark: vivipos.ViviposProduct?.MinStock ?? 0,
            stockHighWatermark: vivipos.ViviposProduct?.MaxStock ?? 0,
            saleToStockUnitRatio: vivipos.ViviposProduct?.StockConversion ?? 0,
            currentStockLevel: vivipos.ViviposProduct?.Stock ?? 0,
            saleUnit: vivipos.ViviposProduct?.SaleUnit ?? '',
            stockUnit: vivipos.ViviposProduct?.StockUnit ?? ''
        },
        scale: {
            scaleItem: vivipos.ViviposProduct?.Scale ?? false,
            weightDecimalPlaces: vivipos.ViviposProduct?.ScalePrecision ?? 0,
            scaleMultiplier: vivipos.ViviposProduct?.ScaleMultiplier ?? 1,
            tare: vivipos.ViviposProduct?.Tare ?? 0
        },
        priceLevels: [
            {
                priceEnable: vivipos.ViviposProduct?.LevelEnable1 ?? false,
                priceLevel: 'Regular',
                price: vivipos.ViviposProduct?.PriceLevel1 ?? 0,
                HALO: vivipos.ViviposProduct?.Halo1 ?? 0,
                LALO: vivipos.ViviposProduct?.Lalo1 ?? 0
            },
            {
                priceEnable: vivipos.ViviposProduct?.LevelEnable2 ?? false,
                priceLevel: 'Regular',
                price: vivipos.ViviposProduct?.PriceLevel2 ?? 0,
                HALO: vivipos.ViviposProduct?.Halo2 ?? 0,
                LALO: vivipos.ViviposProduct?.Lalo2 ?? 0
            },
            {
                priceEnable: vivipos.ViviposProduct?.LevelEnable3 ?? false,
                priceLevel: 'Regular',
                price: vivipos.ViviposProduct?.PriceLevel3 ?? 0,
                HALO: vivipos.ViviposProduct?.Halo3 ?? 0,
                LALO: vivipos.ViviposProduct?.Lalo3 ?? 0
            },
            {
                priceEnable: vivipos.ViviposProduct?.LevelEnable4 ?? false,
                priceLevel: 'Regular',
                price: vivipos.ViviposProduct?.PriceLevel4 ?? 0,
                HALO: vivipos.ViviposProduct?.Halo4 ?? 0,
                LALO: vivipos.ViviposProduct?.Lalo4 ?? 0
            },
            {
                priceEnable: vivipos.ViviposProduct?.LevelEnable5 ?? false,
                priceLevel: 'Regular',
                price: vivipos.ViviposProduct?.PriceLevel5 ?? 0,
                HALO: vivipos.ViviposProduct?.Halo5 ?? 0,
                LALO: vivipos.ViviposProduct?.Lalo5 ?? 0
            },
            {
                priceEnable: vivipos.ViviposProduct?.LevelEnable6 ?? false,
                priceLevel: 'Regular',
                price: vivipos.ViviposProduct?.PriceLevel6 ?? 0,
                HALO: vivipos.ViviposProduct?.Halo6 ?? 0,
                LALO: vivipos.ViviposProduct?.Lalo6 ?? 0
            },
            {
                priceEnable: vivipos.ViviposProduct?.LevelEnable7 ?? false,
                priceLevel: 'Regular',
                price: vivipos.ViviposProduct?.PriceLevel7 ?? 0,
                HALO: vivipos.ViviposProduct?.Halo7 ?? 0,
                LALO: vivipos.ViviposProduct?.Lalo7 ?? 0
            },
            {
                priceEnable: vivipos.ViviposProduct?.LevelEnable8 ?? false,
                priceLevel: 'Regular',
                price: vivipos.ViviposProduct?.PriceLevel8 ?? 0,
                HALO: vivipos.ViviposProduct?.Halo8 ?? 0,
                LALO: vivipos.ViviposProduct?.Lalo8 ?? 0
            },
            {
                priceEnable: vivipos.ViviposProduct?.LevelEnable9 ?? false,
                priceLevel: 'Regular',
                price: vivipos.ViviposProduct?.PriceLevel9 ?? 0,
                HALO: vivipos.ViviposProduct?.Halo9 ?? 0,
                LALO: vivipos.ViviposProduct?.Lalo9 ?? 0
            }
        ],
        condiments: {
            required: vivipos.ViviposProduct?.ForceCondiment ?? false,
            condimentList: vivipos.ViviposProduct?.CondGroup
                ? vivipos.ViviposProduct?.CondGroup.split('|').map((id: string) => ({ id, name: '' }))
                : []
        },
        linkGroups: vivipos.ViviposProduct?.LinkGroup
            ? vivipos.ViviposProduct?.LinkGroup.split('|').map((id: string) => ({
                id,
                name: '',
                appearance: { image: '', buttonColor: '', fontSizes: '', displayMode: '' }
            }))
            : [],
        variation: {
            required: vivipos.ViviposProduct?.ForceVariation ?? false,
            sourceProduct: false,
            variationItem: [
                {
                    id: '',
                    variation: '',
                    productData: {} as Product
                }
            ]
        },
        miscellaneous: {
            requiredMemo: vivipos.ViviposProduct?.ForceMemo ?? false,
            memo: vivipos.ViviposProduct?.Memo ?? '',
            ageVerificationRequired: vivipos.ViviposProduct?.AgeVerification ?? false,
            noMembershipDiscountAllowed: vivipos.ViviposProduct?.NonMembershipDiscountAllowed ?? false,
            nonDiscountable: vivipos.ViviposProduct?.NonDiscountable ?? false,
            nonSurchargeable: vivipos.ViviposProduct?.NonSurchargeable ?? false
        },
        setItems: (vivipos.ViviposSetItem ?? []).map((setItem: ViviposSetItem, idx: number) => {
            let productGroup: ViviposProductGroup | undefined = undefined;
            if (vivipos.ViviposPluGroup && vivipos.ViviposPluGroup[idx]) {
                productGroup = vivipos.ViviposPluGroup[idx];
            }
            return viviposToSetitem(productList, setItem, productGroup);
        }),
    };
}

export function viviposToSetitem(productList: Product[], vivipos: ViviposSetItem, productGroup?: ViviposProductGroup): SetItems {
    return {
        id: vivipos.ID ?? '',
        label: vivipos.Label ?? '',
        basePrice: vivipos.BasePrice ?? 0,
        quantity: vivipos.Quantity ?? 0,
        allowReduction: vivipos.Reduction ?? false,
        linkGroup: setItemToPlsGroup(productGroup),
        presetItem: productList.find(p => p.basic?.id === vivipos.PresetNo) ?? { ...defaultProduct }
    };
}

export function setItemToPlsGroup(productGroup?: ViviposProductGroup): LinkGroups {
    return {
        id: productGroup?.ID ?? '',
        name: productGroup?.Name ?? '',
        appearance: { image: '', buttonColor: '', fontSizes: '', displayMode: '' }
    };
}

export function setItemToVivipos(set_items: SetItems): ViviposSetItem {
    return {
        MID: 1,
        ID: set_items.id,
        PlusetNo: set_items.presetItem?.basic?.productNumber,
        Label: set_items.label,
        PresetNo: set_items.presetItem?.basic?.id,
        Quantity: set_items.quantity,
        BasePrice: set_items.basePrice,
        LinkGroupID: set_items.linkGroup.id,
        Reduction: set_items.allowReduction,
        Created: Date.now(),
        Modified: Date.now(),
        Sequence: 0
    };
}

export const useProductsStore = defineStore('product', {
    state: () => ({
        departmentList: [] as Department[],
        productList: [] as Product[],
        activeDepartment: { ...defaultDepartment },
        activeProduct: { ...defaultProduct },
        tempActiveProduct: { ...defaultProduct }
    }),
    getters: {
        getDepartmentList: (state) => {
            return state.departmentList;
        },
        getActiveDepartment: (state) => {
            return state.activeDepartment;
        },
        findDepartmentById: (state) => (id: string) => {
            return state.departmentList.find(department => department.id === id);
        },
        getActiveProduct: (state) => {
            return state.activeProduct;
        },
        getActiveProductId: (state) => {
            return state.activeProduct.basic.productNumber;
        },
        findProductById: (state) => (id: string) => {
            return state.productList.find((product: Product) => product.basic.productNumber === id);
        },
        getActiveProductList: (state) => {
            return state.productList;
        },
        getTempActiveProduct: (state) => {
            return state.tempActiveProduct;
        },
        getIsActiveProductChanged: (state) => {
            return JSON.stringify(state.activeProduct) !== JSON.stringify(state.tempActiveProduct);
        },
        getAllProducts: (state) => (filter: string, search: string) => {
            let products = state.productList;
            if (filter !== 'all') {
                products = products.filter(product => product.basic.department === filter);
            }
            if (search) {
                products = products.filter(product =>
                    product.basic.productNumber.toLowerCase().includes(search.toLowerCase()) ||
                    product.basic.name.toLowerCase().includes(search.toLowerCase()) ||
                    product.basic.barcode.toLowerCase().includes(search.toLowerCase())
                );
            }
            return products;
        },
        getProductDataById: (state) => (id: string) => {
            return state.productList.find(product => product.basic.productNumber === id);
        }
    },
    actions: {
        initializeDepartments() {
            getAllDepartments().then((response: any[]) => {
                this.departmentList = response.map((item: any) => viviposCategoryToDepartment(item));
                getAllProducts().then((response: any[]) => {
                    const tempProducts: Product[] = response.map((item: any) => viviposToProduct(item, []));
                    const products: Product[] = response.map((item: any, idx: number) => viviposToProduct(item, tempProducts));
                    products.forEach((product: Product) => {
                        const department = this.departmentList.find(dep => dep.name === product.basic.department);
                        if (department) {
                            this.productList.push(product);
                        }
                    });
                }).catch((error: Error) => {
                    console.error('Error fetching products:', error);
                });
            }).catch((error: Error) => {
                console.error('Error fetching categories:', error);
            });
        },
        initActiveDepartment(department: Department) {
            this.activeDepartment = department;
        },
        initializeProducts(products: Product[]) {
            this.productList = products;
        },
        addProduct(product: Product) {
            addProductAPI(product)
                .then(() => getAllDepartments())
                .then((departments: any[]) => {
                    this.departmentList = departments.map((item: any) => viviposCategoryToDepartment(item));
                    getAllProducts().then((response: any[]) => {
                        const tempProducts: Product[] = response.map((item: any) => viviposToProduct(item, []));
                        const products: Product[] = response.map((item: any, idx: number) => viviposToProduct(item, tempProducts));
                        this.productList = [];
                        products.forEach((product: Product) => {
                            const department = this.departmentList.find(dep => dep.name === product.basic.department);
                            if (department) {
                                this.productList.push(product);
                            }
                        });
                    }).catch((error: Error) => {
                        console.error('Error fetching products:', error);
                    });
                })
                .catch((error: Error) => {
                    console.error('Error updating product:', error);
                });
        },
        removeProduct(id: string) {
            deleteProductAPI(id).then(() => {
                this.productList = this.productList.filter(product => product.basic.id !== id);
                getAllDepartments()
                    .then((departments: any[]) => {
                        this.departmentList = departments.map((item: any) => viviposCategoryToDepartment(item));
                        getAllProducts().then((response: any[]) => {
                            const tempProducts: Product[] = response.map((item: any) => viviposToProduct(item, []));
                            const products: Product[] = response.map((item: any, idx: number) => viviposToProduct(item, tempProducts));
                            this.productList = [];
                            products.forEach((product: Product) => {
                                const department = this.departmentList.find(dep => dep.name === product.basic.department);
                                if (department) {
                                    this.productList.push(product);
                                }
                            });
                        }).catch((error: Error) => {
                            console.error('Error fetching products:', error);
                        });
                    })
                    .catch((error: Error) => {
                        console.error('Error updating product:', error);
                    });
            })
        },
        updateProduct(updatedProduct: Product) {
            updateProductAPI(updatedProduct)
                .then(() => getAllDepartments())
                .then((departments: any[]) => {
                    this.departmentList = departments.map((item: any) => viviposCategoryToDepartment(item));
                    getAllProducts().then((response: any[]) => {
                        const tempProducts: Product[] = response.map((item: any) => viviposToProduct(item, []));
                        const products: Product[] = response.map((item: any, idx: number) => viviposToProduct(item, tempProducts));
                        this.productList = [];
                        products.forEach((product: Product) => {
                            const department = this.departmentList.find(dep => dep.name === product.basic.department);
                            if (department) {
                                this.productList.push(product);
                            }
                        });
                    }).catch((error: Error) => {
                        console.error('Error fetching products:', error);
                    });
                })
                .catch((error: Error) => {
                    console.error('Error updating product:', error);
                });
        },
        setActiveProduct(product: Product) {
            this.activeProduct = product;
        },
        setTempActiveProduct(product: Product) {
            this.tempActiveProduct = product;
        },
        initActiveProduct(product: Product) {
            this.activeProduct = product;
            this.tempActiveProduct = product;
        }
    },
});